import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Space } from 'antd';
import { showAlertNotification } from 'components/common';
import { ButtonCommon } from 'components/common/Button/ButtonCommon';
import { InputCommon } from 'components/common/Input/InputCommon';
import PrefixedInput from 'components/common/Input/PrefixedInput';
import LabelWithRequired from 'components/common/Label/LabelWithRequired';
import { useCreateSupplierMutation, useGenerateCodeMutation, useGetStatusQuery } from 'services';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { SupplierFormValues } from 'models/Supplier';
import Supplier from 'models/Supplier';

interface QuickAddSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (supplier: Supplier) => void;
}

const QuickAddSupplierModal: React.FC<QuickAddSupplierModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  const [createSupplier] = useCreateSupplierMutation();
  const [generateCode] = useGenerateCodeMutation();
  const { data: statusData } = useGetStatusQuery({ query: { filter: 'code:eq:pr-active' } });

  // Initialize form with generated code on modal open
  useEffect(() => {
    if (isOpen) {
      initializeForm();
    }
  }, [isOpen]);

  /**
   * Initialize form with generated supplier code
   */
  const initializeForm = async () => {
    try {
      const codeResponse = await generateCode({ code: 'SUP' }).unwrap();
      const newCode = codeResponse || `SUP${Date.now()}`;
      setGeneratedCode(newCode);
      form.setFieldsValue({
        code: newCode,
      });
    } catch (error) {
      console.error('Error generating code:', error);
      const newCode = `SUP${Date.now()}`;
      setGeneratedCode(newCode);
      form.setFieldsValue({
        code: newCode,
      });
    }
  };

  /**
   * Handle generate new supplier code
   */
  const handleCreateCode = async () => {
    try {
      const res = await generateCode({ code: 'SUP' }).unwrap();
      if (res) {
        setGeneratedCode(res);
        form.setFieldsValue({ code: res });
      } else {
        console.error('Code generation failed, no data returned.');
      }
    } catch (error) {
      console.error('Error generating code:', error);
    }
  };

  /**
   * Handle form submission
   * @param addAndSelect - Whether to select the supplier after creation
   */
  const handleSubmit = async (addAndSelect = false) => {
    try {
      const values = await form.validateFields();
      const errors = [];

      // Validate required fields
      if (!values.name || values.name.trim() === '') {
        errors.push({
          name: 'name',
          errors: ['Vui lòng nhập tên nhà cung cấp'],
        });
      }

      if (!values.code || values.code.trim() === '') {
        errors.push({
          name: 'code',
          errors: ['Vui lòng nhập mã nhà cung cấp'],
        });
      }

      if (errors.length > 0) {
        form.setFields(errors);
        return;
      }

      setLoading(true);

      // Get the pr-active status ID
      const statusId = statusData?.data?.[0]?.id || '69ea282b-b7f4-4e26-85fa-a384a63a976d';
      console.log('Using statusId:', statusId);
      const payload: SupplierFormValues = {
        baseUser: {
          name: values.name,
          code: values.code || generatedCode,
          email: values.email || undefined,
          phoneNumber: values.phoneNumber || '',
          address: values.address || '',
          isActive: true,
        },
        supplierContractsInfo: [],
        bankAccountsInfo: [],
        taxCode: values.taxCode || null,
        note: '',
        statusId: statusId,
      };

      const response = await createSupplier(payload).unwrap();

      if (response.success) {
        showAlertNotification({
          type: 'success',
          title: 'Thành công',
          message: `Đã thêm nhà cung cấp "${values.name}" thành công.`,
        });
        form.resetFields();
        setGeneratedCode('');

        if (addAndSelect && response.data) {
          onSuccess?.(response.data);
        }
        onClose();
      }
    } catch (error: unknown) {
      console.error('Error creating supplier:', error);
      const errorMessage =
        (error as { data?: { message?: { message?: string[] } } })?.data?.message?.message?.[0] ||
        'Không thể thêm nhà cung cấp. Vui lòng thử lại.';
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel and close modal
   */
  const handleCancel = () => {
    form.resetFields();
    setGeneratedCode('');
    onClose();
  };

  const codePrefix = (form.getFieldValue('code') || generatedCode || 'SUP')?.substring(0, 6) || 'SUP';

  return (
    <Modal
      title={<div className='text-lg font-semibold p-4'>Thông tin nhà cung cấp</div>}
      open={isOpen}
      onCancel={handleCancel}
      footer={
        <Space size='middle' className='gap-2 lg:gap-3 flex justify-end mr-5' align='center'>
          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-white px-6 text-black'
            onClick={handleCancel}
          >
            Bỏ qua
          </ButtonCommon>

          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-white px-6 text-black'
            loading={loading}
            onClick={() => handleSubmit(false)}
          >
            <span className='whitespace-nowrap'>Lưu nhà cung cấp</span>
          </ButtonCommon>

          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-[var(--primaryColor500)] px-3 gap-2'
            loading={loading}
            onClick={() => handleSubmit(true)}
          >
            <div className='w-5 h-5'>
              <CheckCircleIcon size={20} />
            </div>
            <span className='whitespace-nowrap text-white'>Lưu và chọn</span>
          </ButtonCommon>
        </Space>
      }
      width={900}
      centered
      bodyStyle={{ padding: '20px' }}
    >
      <Form form={form} layout='vertical'>
        <Row gutter={[24, 12]}>
          <Col span={12}>
            <Form.Item label={<LabelWithRequired label='Mã nhà cung cấp' />} name='code' className='mb-0'>
              <PrefixedInput
                prefix={codePrefix || ''}
                placeholder='Nhập 5 số'
                maxEditableLength={5}
                value={form.getFieldValue('code') || generatedCode}
                onChange={(val: string) => form.setFieldsValue({ code: val })}
                disabled={false}
                inputProps={{
                  suffix: (
                    <ButtonCommon
                      color='basic'
                      fullWidth
                      radius='rounded'
                      size='xs'
                      onClick={handleCreateCode}
                      isDisabled={false}
                    >
                      Generate
                    </ButtonCommon>
                  ),
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={<LabelWithRequired label='Tên nhà cung cấp' />}
              name='name'
              required={false}
              className='mb-0'
            >
              <InputCommon type='label' placeholder='Nhập tên nhà cung cấp...' maxLength={100} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label='Email' name='email' className='mb-0'>
              <InputCommon placeholder='Nhập email...' />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label='Số điện thoại' name='phoneNumber' className='mb-0'>
              <InputCommon placeholder='Nhập số điện thoại...' />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label='Mã số thuế' name='taxCode' className='mb-0'>
              <InputCommon placeholder='Nhập mã số thuế...' />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label='Địa chỉ' name='address' className='mb-0'>
              <InputCommon placeholder='Nhập địa chỉ...' />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default QuickAddSupplierModal;
